import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { variantDisplayName } from '@aeris/catalog'
import { Combobox } from '@aeris/ui'

import { fetchProducts } from '../lib/api.ts'
import { useDebounced } from '../lib/use-debounced.ts'

/**
 * Pick a product by typing any part of it.
 *
 * The counterpart of `PartyPicker`, and it exists for a worse version of the
 * same problem. The sales form's field accepted a code or a barcode and
 * matched them EXACTLY: typing "cerveja" found nothing, and the screen said
 * "nenhum produto com esse código", which is true and useless to somebody who
 * does not know the code. A catalogue you can only reach by knowing what is in
 * it is a catalogue with a password on it.
 *
 * `/v1/products` has answered `search` since F007 — over name, SKU and GTIN —
 * so nothing new is asked of the server here. What is new is that the answer
 * arrives while the operator types instead of after they guess right.
 *
 * ## Variants, not products
 *
 * A sale line references a VARIANT, because that is what has a price, a
 * barcode and a stock balance. One product with three sizes is three entries
 * here, told apart by their SKU — which is also why the SKU is the hint rather
 * than a decoration.
 */

export type PickedProduct = {
  readonly variantId: string
  /** O produto da cor (F177): é o que acha as outras cores dele. */
  readonly productId?: string
  readonly productName: string
  readonly sku: string
  readonly unit: string
}

export type ProductPickerProps = {
  value: PickedProduct | null
  onChange: (product: PickedProduct | null) => void
  id?: string
  'aria-describedby'?: string | undefined
  invalid?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
}

export const ProductPicker = ({
  value,
  onChange,
  invalid = false,
  disabled = false,
  placeholder = 'Nome, código ou código de barras',
  className,
  id,
  ...aria
}: ProductPickerProps): ReactNode => {
  const [search, setSearch] = useState('')
  const term = useDebounced(search)

  const results = useQuery({
    queryKey: ['product-picker', term],
    queryFn: ({ signal }) =>
      fetchProducts(
        {
          status: 'active',
          // F247: o arquivado até ter estoque não se vende nem se oferece.
          shelved: 'exclude',
          // Eight rows fit without scrolling; the search is how the ninth is
          // reached, which is the whole point of the control.
          pageSize: 8,
          ...(term === '' ? {} : { search: term }),
        },
        signal
      ),
  })

  /**
   * Only active variants.
   *
   * An archived variant still appears in old documents — section 30 archives
   * rather than deletes — and must not be sellable on a new one.
   */
  const picked = (results.data?.items ?? []).flatMap((product) =>
    product.variants
      .filter((variant) => variant.status === 'active')
      .map((variant) => ({
        variantId: variant.id,
        productId: product.id,
        // Com a cor (F176): "Capa iPhone — Azul" na lista e na linha.
        productName: variantDisplayName(product.name, variant.name),
        sku: variant.sku,
        unit: variant.unit,
      }))
  )

  return (
    <Combobox
      {...(id === undefined ? {} : { id })}
      {...aria}
      value={value?.variantId ?? null}
      onValueChange={(next) => {
        onChange(
          next === null
            ? null
            : (picked.find((item) => item.variantId === next) ?? null)
        )
      }}
      items={picked.map((item) => ({
        value: item.variantId,
        label: item.productName,
        hint: item.sku,
      }))}
      search={search}
      onSearchChange={setSearch}
      // From the caller's own value, not looked up in `items`: the chosen
      // product falls out of the results the moment the next search runs, and
      // a field that blanks itself then looks like it lost the selection.
      selectedLabel={
        value === null ? null : `${value.productName} · ${value.sku}`
      }
      loading={results.isFetching}
      invalid={invalid}
      disabled={disabled}
      placeholder={placeholder}
      emptyMessage={
        term === '' ? 'Digite para buscar.' : `Nenhum produto para "${term}".`
      }
      clearable
      {...(className === undefined ? {} : { className })}
    />
  )
}
